import React, {useMemo} from "react";
import {Link, Stack, Tooltip, Typography} from "@mui/material";
import {useDeskStore} from "../../../api/DeskStore";
import {RemoteValue} from "../../../util/RemoteValue";
import {DateRangeOptions} from "../../../components/filter-fields/DateRangeFilter";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import NeighbourhoodColorIcon from "../../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {
    Desks_Read,
    Employees_Read,
    KeyData_Read,
    Neighbourhoods_Read,
    NewJoiners_Read,
    VisitReports_Read,
} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {
    findNeighbourhoodsWithUnderutilizedAssignedDesks,
    NeighbourhoodDeskUtilizationComparison,
} from "./AssignedDeskFlexUtilization";
import {Link as RouterLink} from "react-router";
import {WidgetEmployeeVisitFigure} from "../WidgetFigure";
import {useDeskModelStore} from "../../../api/DeskModelStore";
import DeskChip from "../../../components/desk-chip/DeskChip";

const dateRange = DateRangeOptions.lastFourMonths;

const csvModel = RemoteValue.fromValue(new CsvModel<NeighbourhoodDeskUtilizationComparison>([
    {header: 'Neighbourhood', value: ({neighbourhood}) => neighbourhood.name},
    {header: 'Flex desk utilization', value: ({flexUtilization}) => `${flexUtilization.percent}%`},
    {header: 'Assigned desks', value: ({assignedDesks}) => assignedDesks.map(({desk}) => desk.name).join('; ')},
    {
        header: 'Assigned desk utilization',
        value: ({assignedDesks}) => assignedDesks
            .map(({desk, utilization}) => `${desk.name}: ${utilization.percent}%`)
            .join('; '),
    },
]));

export function AssignedDesksUsedLessThanFlexDesksWidget() {
    const pageUrl = usePageUrl();
    const desks = useDeskStore(state => state.remoteValue);
    const deskModel = useDeskModelStore(dateRange);
    const comparisons = useMemo(() => RemoteValue
            .combine({deskModel, desks})
            .map(({deskModel, desks}) => findNeighbourhoodsWithUnderutilizedAssignedDesks(deskModel, desks)),
        [deskModel, desks],
    );

    return (
        <DashboardWidget
            title="Assigned desks used less than flex desks"
            description="Neighbourhoods where assigned desks have lower utilization than the neighbourhood's flex desks over the last four months."
            problems={comparisons}
            requiredPermissions={[
                Desks_Read,
                Employees_Read,
                KeyData_Read,
                Neighbourhoods_Read,
                NewJoiners_Read,
                VisitReports_Read,
            ]}
            csvModel={csvModel}
            renderProblem={({neighbourhood, flexUtilization, assignedDesks}, index) => (
                <>
                    <DashboardWidgetItem
                        key={neighbourhood.key}
                        index={index}
                        left={(
                            <Stack gap={1} py={0.5}>
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                    <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                                    <Link
                                        component={RouterLink}
                                        to={pageUrl(AppPages.neighbourhoodVisitReport, undefined, {search: neighbourhood.name})}
                                        underline="hover"
                                    >
                                        {neighbourhood.name}
                                    </Link>
                                </Stack>
                            </Stack>
                        )}
                        right={
                            <Tooltip
                                title={
                                    <Typography variant="body1">
                                        {flexUtilization.sharedDesks.length} shared {flexUtilization.sharedDesks.length === 1 ? 'desk' : 'desks'} {' '}
                                        for {flexUtilization.noDeskEmployees.length} {flexUtilization.noDeskEmployees.length === 1 ? 'employee' : 'employees'} without assigned desks {' '}
                                        who all together visited office {flexUtilization.deskDayUsage} {flexUtilization.deskDayUsage === 1 ? 'time' : 'times'} {' '}
                                        in {flexUtilization.dayCount} {flexUtilization.dayCount === 1 ? 'day' : 'days'}
                                    </Typography>
                                }
                                enterDelay={500}
                            >
                                <Typography variant="body2">
                                    {flexUtilization.sharedDesks.length} shared {flexUtilization.sharedDesks.length === 1 ? 'desk' : 'desks'} for {' '}
                                    {flexUtilization.noDeskEmployees.length} {flexUtilization.noDeskEmployees.length === 1 ? 'employee' : 'employees'} {' '}
                                    <Typography variant="caption" color="text.secondary" component="span">
                                        {flexUtilization.percent}%
                                    </Typography>
                                </Typography>
                            </Tooltip>
                     }
                    />
                    {assignedDesks.map(({desk, employee, utilization}, subIndex) => (
                        <DashboardWidgetItem
                            key={desk.id}
                            index={index}
                            subIndex={subIndex}
                            left={
                                <DeskChip desk={desk} deskAssignee={employee} size="small"/>
                            }
                            right={
                                <WidgetEmployeeVisitFigure employeeVisits={utilization}/>
                            }
                        />
                    ))}
                </>
            )}
        />
    );
}
